import { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import CreatableSelect from 'react-select/creatable';
import { shiftTimeSlots, parseTimeInput } from '../../helpers';

const defaultTheme = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: '#0649b5',
    neutral10: '#edf2f9',
    neutral20: '#d2ddec',
    neutral30: '#d2ddec',
    neutral50: '#b1c2da',
    neutral80: '#12263f',
    dangerLight: '#DEEBFF',
    danger: '#12263f',
  },
});

// Dropdown of fixed time-of-day slots (react-select) that also accepts a
// manually typed time (e.g. "9:07 am"). Picking or typing both resolve to
// the same { value: '24hr HH:MM', label } shape, so callers can treat this
// as a drop-in replacement for <Select options={shiftTimeSlots()} .../>.
//
// react-select never preloads the current value's text into the search
// input (it's a combobox, not a plain text field), so once a manually
// typed time was committed there was no way to backspace/adjust it - the
// only options were "pick a different preset" or retype a whole new value
// blind. To make the field feel editable, the current value's label is
// loaded into the input on focus so it can be edited in place.
const TimeSlotSelect = ({
  name,
  value,
  onChange,
  interval,
  labelFormat,
  placeholder,
  className,
  theme,
  onBlur,
  isClearable,
}) => {
  const [inputValue, setInputValue] = useState('');
  // True only while the user is actively typing/backspacing - not merely
  // focused. react-select falls back to rendering the selected value
  // whenever the input is empty, so without this, backspacing the text away
  // made the old value reappear instead of leaving an empty box.
  const [isEditing, setIsEditing] = useState(false);
  // Selecting an option blurs the field (below), which would otherwise let
  // blur re-commit the text typed *before* the selection - stale, because
  // React batches the input reset within the same event. A ref isn't
  // batched, so it reliably marks "a selection already committed here".
  const justSelectedRef = useRef(false);

  const handleChange = (option) => {
    justSelectedRef.current = true;
    onChange(option);
  };

  const baseOptions = useMemo(() => {
    const slots = shiftTimeSlots(interval);
    if (labelFormat === '24h') {
      return slots.map((slot) => ({ value: slot.value, label: slot.value }));
    }
    return slots;
  }, [interval, labelFormat]);

  // A bare "H:MM"/"HH:MM" (no am/pm) is ambiguous and off the 15-min grid
  // has no matching preset to filter down to, so typing e.g. "4:04" would
  // otherwise dead-end on "No options". Offer both the AM and PM reading
  // as explicit, pickable options instead of forcing the user to also type
  // "am"/"pm" themselves.
  const options = useMemo(() => {
    if (labelFormat !== '12h') return baseOptions;

    const bareMatch = inputValue.trim().match(/^(\d{1,2}):(\d{2})$/);
    if (!bareMatch) return baseOptions;

    const hours = parseInt(bareMatch[1], 10);
    if (hours < 1 || hours > 12) return baseOptions;

    const disambiguated = [
      parseTimeInput(`${inputValue.trim()} am`, labelFormat),
      parseTimeInput(`${inputValue.trim()} pm`, labelFormat),
    ].filter(
      (option) => option && !baseOptions.some((o) => o.value === option.value)
    );

    return disambiguated.length
      ? [...baseOptions, ...disambiguated]
      : baseOptions;
  }, [baseOptions, inputValue, labelFormat]);

  return (
    <CreatableSelect
      name={name}
      options={options}
      className={className}
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      inputValue={inputValue}
      controlShouldRenderValue={!isEditing}
      // Drop focus after a pick so every edit starts from a clean focus,
      // which re-runs the prefill below - otherwise a just-selected value
      // can't be edited in place until the field is re-entered.
      blurInputOnSelect
      onInputChange={(newValue, actionMeta) => {
        setInputValue(newValue);
        setIsEditing(actionMeta.action === 'input-change');
      }}
      onFocus={() => setInputValue(value ? value.label : '')}
      onBlur={(event) => {
        const justSelected = justSelectedRef.current;
        justSelectedRef.current = false;
        if (!justSelected) {
          const typed = parseTimeInput(inputValue, labelFormat);
          if (typed && (!value || typed.value !== value.value)) {
            onChange(typed);
          }
        }
        setIsEditing(false);
        setInputValue('');
        if (onBlur) onBlur(event);
      }}
      isClearable={isClearable}
      theme={theme}
      // Focus prefills the input with the current value's label so it can
      // be edited in place - but until the user actually types/backspaces
      // something (isEditing), that prefilled text shouldn't filter the
      // list down to just the current value. Only filter once there's a
      // real, user-driven search query.
      filterOption={(option, rawInput) => {
        if (!isEditing) return true;
        const candidate = `${option.label} ${option.data.value}`.toLowerCase();
        return candidate.includes(rawInput.trim().toLowerCase());
      }}
      isValidNewOption={(text) => {
        const parsed = parseTimeInput(text, labelFormat);
        return (
          Boolean(parsed) && !options.some((o) => o.value === parsed.value)
        );
      }}
      onCreateOption={(text) => {
        const option = parseTimeInput(text, labelFormat);
        if (option) handleChange(option);
      }}
      formatCreateLabel={(text) => {
        const option = parseTimeInput(text, labelFormat);
        return option ? `Use "${option.label}"` : `Use "${text}"`;
      }}
    />
  );
};

TimeSlotSelect.propTypes = {
  name: PropTypes.string,
  value: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  interval: PropTypes.number,
  labelFormat: PropTypes.oneOf(['12h', '24h']),
  placeholder: PropTypes.string,
  className: PropTypes.string,
  theme: PropTypes.func,
  onBlur: PropTypes.func,
  isClearable: PropTypes.bool,
};

TimeSlotSelect.defaultProps = {
  name: undefined,
  value: null,
  interval: 15,
  labelFormat: '12h',
  placeholder: undefined,
  className: 'basic-multi-select',
  theme: defaultTheme,
  onBlur: undefined,
  isClearable: false,
};

export default TimeSlotSelect;
